import React, { useEffect } from 'react';
import {
  Box,
  Drawer,
  List,
  ListItemIcon,
  ListItemText,
  ListItemButton,
  Toolbar,
  CssBaseline,
  IconButton,
  Avatar,
  Tooltip,
  Divider,
  Typography,
  Chip,
} from '@mui/material';
import { useQuery } from 'react-query';
import api from './utils/api';
import {
  Dashboard as DashboardIcon,
  Event as AppointmentsIcon,
  LocalHospital as ClinicalIcon,
  Receipt as BillingIcon,
  Inventory as InventoryIcon,
  People as PeopleIcon,
  Assessment as ReportsIcon,
  Logout as LogoutIcon,
  Settings as SettingsIcon,
  Menu as MenuIcon,
  Warehouse as BarnIcon,
  HelpOutline as HelpOutlineIcon,
} from '@mui/icons-material';
import { Link, useLocation, Routes, Route, Navigate, useParams } from 'react-router-dom';
import { faro } from './config/faro';
import { identifyAmplitudeUser, resetAmplitudeUser } from './config/amplitude';
import { startInHouseAnalytics, trackInHousePageView } from './analytics/inhouse';
import { useRBAC } from './auth/useRBAC';
import {
  REPORTS_NOUN,
  REPORTS_PERMISSION,
  REPORTS_SHOW_WHILE_PENDING,
  canOpenReports,
} from './auth/canOpenReports';
import { CustomScreenRoute, ScreenRoute } from './auth/ScreenRoute';
import { SCREEN_ACCESS, screenAccessUnknown, showMenuItem } from './auth/screenAccess';
import type { PermissionSlug } from './services/userPermissionsService';
import { PermissionsUnavailableBanner } from './auth/PermissionsUnavailableBanner';
import { useFaroRouteTracking } from './hooks/useFaroRouteTracking';
import { useAutoDetectClinicTimezone } from './hooks/useAutoDetectClinicTimezone';
import Dashboard from './pages/Dashboard';
import Appointments from './pages/Appointments';
import HorseProfile from './pages/ClinicalRecords/HorseProfile';
import Billing from './pages/Billing';
import BulkMarkPaid from './pages/Billing/BulkMarkPaid';
import Inventory from './pages/Inventory';
import PatientList from './pages/PatientList';
import Clients from './pages/Clients/index';
import Reports from './pages/Reports';
import RefillRequests from './pages/RefillRequests';
import UnallocatedResultsPage from './pages/Inbox/InboxPage';
import AiAssistant from './components/AiAssistant';
import LegacyAiCommandWidget from './components/AiAssistant/LegacyAiCommandWidget';
import FeedbackWidget from './components/FeedbackWidget';
import HorseshoeIcon from './components/HoofIcon';
import AuthProvider from './auth/AuthProvider';
import SessionReauthDialog from './components/SessionReauthDialog';
import WorkstationLock from './components/WorkstationLock';
import UserMenu from './components/UserMenu';
import { OFFLINE_ENABLED, OfflineProvider, OfflineIndicator, withOfflineFallback, skipWhenOffline, readCurrentUser } from './offline';
import { isEffectivelyOnline } from './offline/connectivity';
import { ENTITIES } from './offline/entities';
import { decideNeedsOnboarding } from './auth/onboardingGate';
import { ROLE_SHORT_LABELS } from './auth/roles';
import { useAuth } from './auth/useAuth';
import AuthGuard from './auth/AuthGuard';
import { useAiAssistantEnabled } from './auth/featureFlags';
import Login from './pages/Login';
import Signup from './pages/Signup';
import Paywall from './pages/Paywall';
import Settings from './pages/Settings';
import AccountSetup from './pages/AccountSetup';
import Onboarding from './pages/Onboarding';
import ResetPassword from './pages/ResetPassword';
import VerifyEmail from './pages/VerifyEmail';
import VerifyEmailWall from './pages/VerifyEmailWall';
import Terms from './pages/Terms';
import Privacy from './pages/Privacy';
import SubscriptionSuccess from './pages/SubscriptionSuccess';
import SubscriptionGuard from './components/SubscriptionGuard';
import DeliveryAttestationGuard from './components/DeliveryAttestationGuard';
import ReleaseNotesProvider from './components/ReleaseNotesDialog/ReleaseNotesProvider';
import HelpDropdown from './components/HelpDropdown';
import WhatsNewDialog from './components/WhatsNewDialog';
import BarnOperationsPage from './pages/BarnOperations';
import Barns from './pages/Barns';
import BarnDetail from './pages/Barns/BarnDetail';
import BarnVisitWorkspace from './pages/Barns/BarnVisitWorkspace';
import TrialBanner, { shouldShowTrialBanner } from './components/TrialBanner';
import { hasPaymentGrace, PaymentGraceBanner, PaymentGraceDialog } from './components/PaymentGraceNotice';
import { getSubscriptionStatus } from './services/subscriptionService';
import { OnboardingProvider } from './onboarding/OnboardingProvider';
import { OnboardingLauncherPill } from './onboarding/components/OnboardingLauncherPill';
import { SetupChecklistDrawer } from './onboarding/components/SetupChecklistDrawer';
import { WelcomeModal } from './onboarding/components/WelcomeModal';
import { CompletionCelebration } from './onboarding/components/CompletionCelebration';
import { TourRunner } from './onboarding/components/TourRunner';
import HospitalBoard from './pages/Hospital';
import TreatmentSheetPage from './pages/Hospital/TreatmentSheetPage';

const drawerWidth = 265;

const DEV_BYPASS = process.env.REACT_APP_DEV_BYPASS_AUTH === 'true';

/**
 * Preserves `:horseId` from the legacy `/clinical-records/:horseId` URL when
 * forwarding to the new `/patients/:horseId` route. Plain <Navigate> would
 * drop the dynamic segment.
 */
const LegacyHorseRedirect: React.FC = () => {
  const { horseId } = useParams<{ horseId: string }>();
  return <Navigate to={`/patients/${horseId}`} replace />;
};

// Merged sidebar: "Patients" subsumes the old "Horses" + "Clinical" entries
// since the patient chart is now the canonical place for clinical work.
interface MenuItem {
  text: string;
  icon: React.ReactElement;
  path: string;
  tourKey: string;
  beta?: boolean;
  /**
   * Shown only to someone holding EVERY slug here — the same conjunction the screen's route
   * asks for, taken from `auth/screenAccess.ts` rather than retyped, so the nav and the router
   * cannot drift. An item with no `requires` is always shown (Dashboard, Settings).
   *
   * Reports was the first user, as a single string: it was `ownerOnly` (a ROLE check) until
   * 2026-09-21, when the backend replaced `@Roles(OWNER)` on ReportsController with
   * `view:reports` so an owner can delegate the page from Settings → Team members (ADR-0035 A8).
   * Following the permission rather than the role is what makes that grant visible here.
   *
   * ⚠ `view:reports` is asked through `auth/canOpenReports.ts` rather than through
   * `hasPermission` directly, HERE and in `ReportsRoute` below — one answer, so the
   * menu and the router cannot disagree. Its OWNER arm covers the deploy-skew window
   * in which a new web bundle meets a backend that does not know the slug (the two
   * Render services deploy and roll back independently), and it is sound because the
   * slug is owner-locked server-side. Every OTHER slug stays the plain read.
   *
   * ⚠ HIDING IS CONDITIONAL ON KNOWING (INV-108). `showMenuItem` hides an item only once the
   * permission set has ANSWERED; while it is pending, or when the read failed outright, every
   * item is shown. The alternative — filtering on the empty set a fresh `useRBAC()` consumer
   * starts with — empties the sidebar on every cold load and re-fills it a beat later.
   */
  requires?: readonly PermissionSlug[];
  /**
   * Show this item while the permission set is still PENDING? Taken from the screen's own
   * row (`SCREEN_ACCESS.<key>.showWhilePending`, true for all of them) or, for Reports,
   * from `REPORTS_SHOW_WHILE_PENDING`, which is the one `false` in the app: it is the
   * section most of a clinic does not have, so it was the one that appeared on every cold
   * load and then vanished. An item with no `requires` never reaches this question.
   */
  showWhilePending?: boolean;
}

const menuItems: MenuItem[] = [
  { text: 'Dashboard', icon: <DashboardIcon />, path: '/', tourKey: 'nav-dashboard' },
  { text: 'Schedule', icon: <AppointmentsIcon />, path: '/appointments', tourKey: 'nav-schedule', requires: SCREEN_ACCESS.schedule.view, showWhilePending: SCREEN_ACCESS.schedule.showWhilePending },
  { text: 'Patients', icon: <HorseshoeIcon />, path: '/patients', tourKey: 'nav-patients', requires: SCREEN_ACCESS.patients.view, showWhilePending: SCREEN_ACCESS.patients.showWhilePending },
  { text: 'Clients', icon: <PeopleIcon />, path: '/clients', tourKey: 'nav-clients', requires: SCREEN_ACCESS.clients.view, showWhilePending: SCREEN_ACCESS.clients.showWhilePending },
  { text: 'Barns', icon: <BarnIcon />, path: '/barns', tourKey: 'nav-barns', requires: SCREEN_ACCESS.barns.view, showWhilePending: SCREEN_ACCESS.barns.showWhilePending },
  { text: 'Refill Requests', icon: <ClinicalIcon />, path: '/refill-requests', tourKey: 'nav-refill-requests', requires: SCREEN_ACCESS.refillRequests.view, showWhilePending: SCREEN_ACCESS.refillRequests.showWhilePending },
  { text: 'Reports', icon: <ReportsIcon />, path: '/reports', tourKey: 'nav-reports', requires: [REPORTS_PERMISSION as PermissionSlug], showWhilePending: REPORTS_SHOW_WHILE_PENDING },
  { text: 'Inventory', icon: <InventoryIcon />, path: '/inventory', tourKey: 'nav-inventory', requires: SCREEN_ACCESS.inventory.view, showWhilePending: SCREEN_ACCESS.inventory.showWhilePending },
  { text: 'Invoicing', icon: <BillingIcon />, path: '/billing', tourKey: 'nav-billing', requires: SCREEN_ACCESS.invoicing.view, showWhilePending: SCREEN_ACCESS.invoicing.showWhilePending },
  { text: 'Treatment Sheet', icon: <ClinicalIcon />, path: '/hospital', tourKey: 'nav-treatment-sheet', beta: true, requires: SCREEN_ACCESS.treatmentSheet.view, showWhilePending: SCREEN_ACCESS.treatmentSheet.showWhilePending },
  { text: 'Settings', icon: <SettingsIcon />, path: '/settings', tourKey: 'nav-settings' },
];

// Route wrapper for the Reports page, whose rule is its OWN — `auth/canOpenReports.ts` — and not
// the `SCREEN_ACCESS` table's, because `view:reports` is owner-locked server-side and no other
// `view:` slug is. It replaced `OwnerRoute` on 2026-09-21, when the backend stopped gating
// ReportsController on @Roles(OWNER) — the web must follow the same axis the server enforces, or
// an owner's grant would open the API and leave the nav item hidden.
//
// The DECISION (wait → allow → unknown → refuse) lives in `auth/ScreenRoute.tsx`, shared with
// every screen A1 gated, so the two cannot answer the same question differently:
//
// ⚠ THE WAIT IS THE LOAD-BEARING PART, and it is TWO conditions, not one. `OwnerRoute` waited on
// `loading` because a slow token fetch flash-redirected a real owner (the blank-/reports bug
// useRBAC.test.tsx:312 documents). Permissions are no longer claim-borne (INV-104):
// `jwt.strategy` resolves them from Postgres and the web fetches them separately, so `loading`
// can be false while the SET is still unanswered — `permissionsPending` is that window.
//
// ⚠ A FAILED READ IS NOT A REFUSAL, and a refusal is no longer a redirect. `permissionsPending`
// closes when the read STOPS, not when it succeeds, so an unanswered set renders the PAGE for an
// OWNER (the role arm is TRUE rather than optimistic: the slug is owner-locked) and one calm
// sentence for everyone else. A person who may NOT open Reports now reads that sentence too,
// instead of being bounced to `/` with no explanation — the same ending as every other screen.
const ReportsRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <CustomScreenRoute noun={REPORTS_NOUN} allowed={canOpenReports}>
    {children}
  </CustomScreenRoute>
);

// Mounts the offline read-replica provider only when REACT_APP_OFFLINE_MODE is
// on, so the live app is completely unaffected by default.
const MaybeOffline: React.FC<{ clinicId?: string; userId?: string; viewable?: string[]; children: React.ReactNode }> = ({ clinicId, userId, viewable, children }) =>
  OFFLINE_ENABLED ? <OfflineProvider clinicId={clinicId} userId={userId} viewable={viewable}>{children}</OfflineProvider> : <>{children}</>;

const AuthenticatedLayout = () => {
  const location = useLocation();
  const { user, logout } = useAuth();
  // permissionsUnavailable: /users/me could not be read (after its one retry).
  // Surfaced below so a fail-closed empty set is never mistaken for "this account
  // holds nothing" — see PermissionsUnavailableBanner. ⚠ It does not always mean
  // the set is empty: a TRANSIENT failure keeps the last confirmed answer, and the
  // banner then only reports that it could not be re-checked.
  // permissionsFailure decides the WORDING: the banner must not call a 403 or an
  // unresolvable clinic drift "a connection problem" and offer a Reload that
  // cannot help. Both come from the shared store, so this one banner speaks for
  // every useRBAC instance on the screen.
  const {
    claims,
    loading,
    permissionsPending,
    permissionsUnavailable,
    permissionsFailure,
    hasPermission,
  } = useRBAC();
  // THE MENU HIDES ONLY WHAT IT KNOWS THE PERSON CANNOT OPEN (INV-108). `known` is both windows
  // closed — this layout's own token round trip (`loading`) and the shared /users/me read
  // (`permissionsPending`) — AND an identity to attach the answer to. Filtering before the two
  // windows close would empty the sidebar on every cold load; filtering on a read that FAILED
  // (`menuAccessUnknown`) would empty it for a network blip. Both show everything instead, and
  // the route says so in place if a hidden-but-shown item is clicked.
  //
  // ⚠ `claims !== null` IS THE THIRD CLAUSE AND IT IS NOT REDUNDANT (INV-107 trap (a)). When the
  // token refresh rejects with nothing cached, `loading` closes with `claims` still null — the
  // hook has stopped asking and does not know who this is. `hasPermission` answers false for
  // everything in that window, so without this clause every gated item disappeared from the
  // sidebar of a person who holds them all. It is the same rule `screenAccessVerdict` applies for
  // the route (`claims === null` → `unknown`, never `deny`), spelled for the menu.
  const menuAccessKnown = !loading && !permissionsPending && claims !== null;
  const menuAccessUnknown = screenAccessUnknown(claims, permissionsUnavailable);
  // Fix 87: the view slugs the offline copy may keep, only once the answer is known; an unknown set
  // wipes nothing (a failed read must never empty a device that is about to lose its signal).
  const offlineViewable = menuAccessKnown && !menuAccessUnknown
    ? Array.from(new Set(ENTITIES.flatMap((e) => (e.view && hasPermission(e.view) ? [e.view] : []))))
    : undefined;
  // Reinsight kill switch (GET /public/auth-config), tri-state: null until the
  // backend answers (mount nothing — no flash/swap), true → Reinsight, false →
  // the legacy /ai/command widget prod has always shown. Flipping the env swaps
  // assistants with no deploy.
  const aiAssistantEnabled = useAiAssistantEnabled();
  // Set the practice time zone once, from an OWNER/ADMIN's browser, if nobody
  // ever has. Mounted HERE because this is the first place the clinic record and
  // the user's role are both in hand; the hook is a no-op on every other path.
  // ADR-0034.
  useAutoDetectClinicTimezone();
  const [whatsNewOpen, setWhatsNewOpen] = React.useState(false);
  const [mobileOpen, setMobileOpen] = React.useState(false);

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const { data: backendUser } = useQuery(
    ['current-user-me'],
    // Offline: serve the replica user row so the avatar/greeting keep the
    // vet's name instead of degrading to an email initial.
    withOfflineFallback(
      async () => {
        const res = await api.get('/users/me');
        return res.data;
      },
      () => readCurrentUser(user?.uid, user?.email ?? undefined),
    ),
    { staleTime: 5 * 60 * 1000, enabled: !!user }
  );

  const { data: subscriptionStatus } = useQuery(
    ['subscription-status-banner'],
    getSubscriptionStatus,
    // Not replicated — skip the doomed request offline (banner simply absent).
    { staleTime: 5 * 60 * 1000, enabled: !!user && skipWhenOffline() }
  );
  const graceDays = subscriptionStatus?.paymentGraceDaysRemaining;


  const getUserInitials = () => {
    // Source from `name` (the field edited in Account settings), then the
    // legacy firstName, then email. A full name yields two-letter initials.
    const source = backendUser?.name || backendUser?.firstName;
    if (source) {
      const parts = String(source).trim().split(/\s+/).filter(Boolean);
      if (parts.length >= 2) return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
      if (parts.length === 1) return parts[0][0].toUpperCase();
    }
    if (user?.email) return user.email[0].toUpperCase();
    return '?';
  };

  const getRoleLabel = () => {
    if (!claims?.role) return '';
    // ⚠ `|| claims.role` is NOT dead code, even though ROLE_SHORT_LABELS is TOTAL
    // over the union. The role is a bare string at runtime: the offline claims
    // cache re-reads it from localStorage and casts it (useRBAC.ts:186), so a
    // tampered or pre-upgrade blob can carry a value outside the union. Showing
    // that raw string beats showing `undefined`.
    return ROLE_SHORT_LABELS[claims.role] || claims.role;
  };

  const isNavSelected = (path: string) =>
    path === '/' ? location.pathname === '/' : location.pathname.startsWith(path);

  const navItemSx = (selected: boolean) => ({
    py: 1.1,
    px: 2,
    mb: 0.4,
    mx: 0.5,
    borderRadius: 2,
    ...(selected && {
      bgcolor: '#ffffff',
      boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
      borderLeft: '3px solid #3B2E1A',
      pl: 1.6,
      '&:hover': { bgcolor: '#ffffff' },
    }),
    ...(!selected && {
      '&:hover': { bgcolor: 'rgba(59,46,26,0.08)' },
    }),
  });

  const getDisplayName = () => {
    // Reflect the name the user sets in Account settings (`name`); fall back to
    // the legacy firstName, then a humanized email local-part.
    if (backendUser?.name) return backendUser.name;
    if (backendUser?.firstName) return backendUser.firstName;
    if (user?.email) return user.email.split('@')[0].replace(/[._-]/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
    return '';
  };

  const renderDrawerContent = (onNavClick?: () => void) => (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {/* Logo */}
      <Box sx={{ display: 'flex', justifyContent: 'center', pt: 4, pb: 4, px: 3 }}>
        <Box
          component="img"
          src="/stabletrack-logo-horizontal.png"
          alt="StableTrack"
          sx={{ height: 50, objectFit: 'contain' }}
        />
      </Box>

      {/* Navigation */}
      <List data-tour="sidebar" sx={{ px: 1.5, flexGrow: 1 }}>
        {menuItems.filter((item) => showMenuItem(item.requires, {
          known: menuAccessKnown,
          accessUnknown: menuAccessUnknown,
          showWhilePending: item.showWhilePending,
          // Reports is the ONE slug with a rule of its own: `canOpenReports(claims, hasPermission)`
          // adds the OWNER arm the deploy-skew window needs. Every other slug is the plain read.
          hasPermission: (slug) => (slug === REPORTS_PERMISSION ? canOpenReports(claims, hasPermission) : hasPermission(slug)),
        })).map((item) => {
          const selected = isNavSelected(item.path);
          return (
            <ListItemButton
              key={item.text}
              component={Link}
              to={item.path}
              selected={selected}
              onClick={onNavClick}
              data-tour={item.tourKey}
              sx={navItemSx(selected)}
            >
              <ListItemIcon sx={{ color: selected ? '#3B2E1A' : '#A0744D', minWidth: 36, '& .MuiSvgIcon-root': { fontSize: '1.3rem' } }}>
                {item.icon}
              </ListItemIcon>
              <ListItemText
                primary={item.text}
                sx={{
                  '& .MuiListItemText-primary': {
                    fontSize: '0.9375rem',
                    fontWeight: selected ? 700 : 500,
                    color: selected ? '#3B2E1A' : '#3D2B1F',
                  },
                }}
              />
              {item.beta && (
                <Chip
                  label="Beta"
                  size="small"
                  sx={{
                    ml: 1,
                    height: 18,
                    fontSize: '0.65rem',
                    fontWeight: 600,
                    bgcolor: '#C9772B',
                    color: '#fff',
                    '& .MuiChip-label': { px: 0.75 },
                  }}
                />
              )}
            </ListItemButton>
          );
        })}
      </List>

      {/* Trial banner — shown only inside the last TRIAL_BANNER_DAYS_REMAINING
          days of the trial (product decision 2026-08-31; see TrialBanner.tsx).
          This is the one place the banner's show/hide is decided. */}
      {subscriptionStatus?.isFreeTrial &&
        shouldShowTrialBanner(subscriptionStatus.trialDaysRemaining) && (
          <TrialBanner daysRemaining={subscriptionStatus.trialDaysRemaining} />
        )}
      {hasPaymentGrace(graceDays) && (
        <PaymentGraceBanner daysRemaining={graceDays} canManage={!!subscriptionStatus?.canManage} />
      )}

      {/* User info */}
      <Divider sx={{ borderColor: 'rgba(139,94,52,0.08)' }} />
      <Box sx={{ px: 2.5, py: 2, display: 'flex', alignItems: 'center', gap: 1.5 }}>
        <Avatar
          sx={{
            width: 36,
            height: 36,
            bgcolor: '#6B4F2E',
            fontSize: '0.8rem',
            fontWeight: 600,
          }}
        >
          {getUserInitials()}
        </Avatar>
        <Box sx={{ flexGrow: 1, minWidth: 0 }}>
          <Typography
            variant="body2"
            noWrap
            sx={{ fontWeight: 600, color: '#3B2E1A', fontSize: '0.875rem' }}
          >
            {getDisplayName()}
          </Typography>
          <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.7rem' }}>
            {getRoleLabel()}
          </Typography>
        </Box>
        <Tooltip title="Logout">
          <IconButton size="small" onClick={logout} sx={{ color: 'text.secondary' }}>
            <LogoutIcon sx={{ fontSize: 18 }} />
          </IconButton>
        </Tooltip>
      </Box>
    </Box>
  );

  return (
    <MaybeOffline clinicId={claims?.clinicId || undefined} userId={user?.uid || undefined} viewable={offlineViewable}>
    <Box sx={{ display: 'flex' }}>
      <CssBaseline />

      {/* Mobile top bar */}
      <Toolbar
        sx={{
          display: { xs: 'flex', sm: 'none' },
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          zIndex: (theme) => theme.zIndex.drawer + 1,
          bgcolor: '#FAF9F6',
          minHeight: '48px !important',
          px: 1,
          borderBottom: '1px solid rgba(139,69,19,0.1)',
        }}
      >
        <IconButton onClick={handleDrawerToggle} sx={{ color: '#8B4513' }}>
          <MenuIcon />
        </IconButton>
      </Toolbar>

      <Box
        component="nav"
        sx={{ width: { sm: drawerWidth }, flexShrink: { sm: 0 } }}
      >
        {/* Mobile drawer */}
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{ keepMounted: true }}
          sx={{
            display: { xs: 'block', sm: 'none' },
            '& .MuiDrawer-paper': {
              width: drawerWidth,
              boxSizing: 'border-box',
              bgcolor: '#F5EDE3',
              borderRight: 'none',
            },
          }}
        >
          {renderDrawerContent(handleDrawerToggle)}
        </Drawer>

        {/* Desktop drawer */}
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', sm: 'block' },
            '& .MuiDrawer-paper': {
              width: drawerWidth,
              boxSizing: 'border-box',
              bgcolor: '#F5EDE3',
              borderRight: 'none',
            },
          }}
          open
        >
          {renderDrawerContent()}
        </Drawer>
      </Box>

      <Box
        component="main"
        sx={{
          flexGrow: 1,
          width: { sm: `calc(100% - ${drawerWidth}px)` },
          mt: { xs: '48px', sm: 0 },
          overflowX: 'hidden',
          overflowY: 'auto',
          // Fixed height (instead of minHeight) so position: sticky works
          // for descendants. With minHeight the main grew to fit its content
          // and the window became the scroller — sticky elements couldn't
          // anchor to the main viewport.
          height: { xs: 'calc(100vh - 48px)', sm: '100vh' },
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {/* Top header bar */}
        <Box
          sx={{
            display: { xs: 'none', sm: 'flex' },
            alignItems: 'center',
            justifyContent: 'space-between',
            px: 3,
            py: 1,
            borderBottom: '1px solid',
            borderColor: 'divider',
            bgcolor: 'background.paper',
            flexShrink: 0,
            minHeight: 48,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <HelpDropdown
              onOpenWhatsNew={() => setWhatsNewOpen(true)}
            />
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <OnboardingLauncherPill />
            <UserMenu initials={getUserInitials()} />
          </Box>
        </Box>

        <OfflineIndicator />
        <PermissionsUnavailableBanner
          unavailable={permissionsUnavailable}
          failure={permissionsFailure}
        />
        <Box sx={{ p: { xs: 2, sm: 3 }, flexGrow: 1 }}>
        <Routes>
          <Route
            path="/"
            element={
              <AuthGuard>
                <Dashboard />
              </AuthGuard>
            }
          />
          <Route
            path="/appointments"
            element={
              <AuthGuard>
                <ScreenRoute screen="schedule">
                  <Appointments />
                </ScreenRoute>
              </AuthGuard>
            }
          />
          <Route
            path="/clients"
            element={
              <AuthGuard>
                <ScreenRoute screen="clients">
                  <Clients />
                </ScreenRoute>
              </AuthGuard>
            }
          />
          <Route
            path="/patients"
            element={
              <AuthGuard>
                <ScreenRoute screen="patients">
                  <PatientList />
                </ScreenRoute>
              </AuthGuard>
            }
          />
          <Route
            path="/patients/:horseId"
            element={
              <AuthGuard>
                <ScreenRoute screen="patients">
                  <HorseProfile />
                </ScreenRoute>
              </AuthGuard>
            }
          />
          {/* Legacy /clinical-records routes — preserved as redirects so any
              external links (emails, bookmarks, audit logs) keep resolving
              after the Wave 3 merge into the unified Patients surface. The
              literal "unallocated" still routes to the Inbox triage page. */}
          <Route
            path="/clinical-records"
            element={<Navigate to="/patients" replace />}
          />
          <Route
            path="/clinical-records/unallocated"
            element={
              <AuthGuard>
                <ScreenRoute screen="resultsInbox">
                  <UnallocatedResultsPage />
                </ScreenRoute>
              </AuthGuard>
            }
          />
          <Route
            path="/clinical-records/:horseId"
            element={<LegacyHorseRedirect />}
          />
          <Route
            path="/reports"
            element={
              <AuthGuard>
                <ReportsRoute>
                  <Reports />
                </ReportsRoute>
              </AuthGuard>
            }
          />
          <Route
            path="/refill-requests"
            element={
              <AuthGuard>
                <ScreenRoute screen="refillRequests">
                  <RefillRequests />
                </ScreenRoute>
              </AuthGuard>
            }
          />
          <Route
            path="/billing"
            element={
              <AuthGuard>
                <ScreenRoute screen="invoicing">
                  <Billing />
                </ScreenRoute>
              </AuthGuard>
            }
          />
          <Route
            path="/billing/bulk-paid"
            element={
              <AuthGuard>
                <ScreenRoute screen="invoicing">
                  <BulkMarkPaid />
                </ScreenRoute>
              </AuthGuard>
            }
          />
          <Route
            path="/barn-operations"
            element={
              <AuthGuard>
                <ScreenRoute screen="barnOperations">
                  <BarnOperationsPage />
                </ScreenRoute>
              </AuthGuard>
            }
          />
          <Route
            path="/barns"
            element={
              <AuthGuard>
                <ScreenRoute screen="barns">
                  <Barns />
                </ScreenRoute>
              </AuthGuard>
            }
          />
          <Route
            path="/barns/:id"
            element={
              <AuthGuard>
                <ScreenRoute screen="barns">
                  <BarnDetail />
                </ScreenRoute>
              </AuthGuard>
            }
          />
          <Route
            path="/barn-visits/:visitGroupId"
            element={
              <AuthGuard>
                <ScreenRoute screen="barnVisits">
                  <BarnVisitWorkspace />
                </ScreenRoute>
              </AuthGuard>
            }
          />
          <Route
            path="/inventory"
            element={
              <AuthGuard>
                <ScreenRoute screen="inventory">
                  <Inventory />
                </ScreenRoute>
              </AuthGuard>
            }
          />
          <Route
            path="/settings"
            element={
              <AuthGuard>
                <Settings />
              </AuthGuard>
            }
          />
          <Route
            path="/hospital"
            element={
              <AuthGuard>
                <ScreenRoute screen="treatmentSheet">
                  <HospitalBoard />
                </ScreenRoute>
              </AuthGuard>
            }
          />
          <Route
            path="/hospital/treatment-sheets/:id"
            element={
              <AuthGuard>
                <ScreenRoute screen="treatmentSheet">
                  <TreatmentSheetPage />
                </ScreenRoute>
              </AuthGuard>
            }
          />
          {/* DEA Logs moved into the Inventory page; keep the old URL working. */}
          <Route path="/dea-logs" element={<Navigate to="/inventory?tab=dea-logs" replace />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
        {aiAssistantEnabled === true && <AiAssistant />}
        {aiAssistantEnabled === false && <LegacyAiCommandWidget />}
        <FeedbackWidget />
        <SetupChecklistDrawer />
        <WelcomeModal />
        <CompletionCelebration />
        <TourRunner />
        <WhatsNewDialog
          open={whatsNewOpen}
          onClose={() => setWhatsNewOpen(false)}
        />
        {/* Mounted once here, not in the drawer content, which renders twice (mobile + desktop). */}
        {user && subscriptionStatus?.canManage && hasPaymentGrace(graceDays) && (
          <PaymentGraceDialog daysRemaining={graceDays} userId={user.uid} />
        )}
        </Box>
      </Box>
    </Box>
    </MaybeOffline>
  );
};

const FaroUserSync: React.FC = () => {
  const { user } = useAuth();
  const { claims } = useRBAC();

  const { data: backendUser } = useQuery(
    ['current-user-me'],
    // Same offline fallback as the layout's instance — they share a query key,
    // so the fns must behave identically or the cache flip-flops by mount order.
    withOfflineFallback(
      async () => {
        const res = await api.get('/users/me');
        return res.data;
      },
      () => readCurrentUser(user?.uid, user?.email ?? undefined),
    ),
    { staleTime: 5 * 60 * 1000, enabled: !!user },
  );

  useEffect(() => {
    if (user && claims) {
      // uid only — the uid is enough to correlate a session with a user in our
      // own systems, so there is no reason to hand a staff email address to a
      // third-party RUM store (which, unlike the backend Sentry pipeline, has no
      // scrubbing layer of its own).
      faro?.api.setUser({
        id: user.uid,
        attributes: {
          clinicId: claims.clinicId || '',
          role: claims.role || '',
        },
      });

      identifyAmplitudeUser(
        user.uid,
        {
          clinicId: claims.clinicId || undefined,
          clinicName: backendUser?.clinic?.name || undefined,
          role: claims.role || undefined,
          email: user.email || undefined,
          name: backendUser?.name || user.displayName || user.email || undefined,
          subscriptionTier: backendUser?.clinic?.subscriptionTier || undefined,
          subscriptionStatus: backendUser?.clinic?.subscriptionStatus || undefined,
          website: backendUser?.clinic?.website || undefined,
          servicesOffered: backendUser?.clinic?.servicesOffered || undefined,
          teamSize: backendUser?.clinic?.teamSize || undefined,
          referralSource: backendUser?.clinic?.referralSource || undefined,
          currentSoftware: backendUser?.clinic?.currentSoftware || undefined,
        },
        {
          signupDate: backendUser?.createdAt || undefined,
        },
      );
    } else {
      faro?.api.resetUser();
      resetAmplitudeUser();
    }
  }, [user, claims, backendUser]);

  return null;
};

const AppContent = () => {
  const { isAuthenticated, isLoading, user, graceMode } = useAuth();
  const location = useLocation();
  useFaroRouteTracking();

  // Setup-completion signal. The wizard is reachable only if the user returns
  // signed in after verifying their email; verifying on another device sends
  // them to /login instead, and nothing used to bring the wizard back — so
  // those clinics ran forever with no survey, no terms and no Slack alert.
  // Re-offer setup until it is actually submitted.
  const { data: onboardingUser, isError: onboardingCheckFailed } = useQuery(
    ['current-user-me'],
    withOfflineFallback(
      async () => {
        const res = await api.get('/users/me');
        return res.data;
      },
      () => readCurrentUser(user?.uid, user?.email ?? undefined),
    ),
    { staleTime: 5 * 60 * 1000, enabled: !!user },
  );

  // In-house product analytics: start the batch/beacon loop once, and emit a
  // de-duplicated page_view on each authenticated route change (mirrors the
  // Amplitude autocapture pageViews the in-house pipeline replaces).
  useEffect(() => {
    startInHouseAnalytics();
  }, []);
  useEffect(() => {
    if (isAuthenticated) trackInHousePageView(location.pathname);
  }, [isAuthenticated, location.pathname]);

  // Public routes that don't require authentication
  const publicRoutes = ['/login', '/signup', '/setup', '/reset-password', '/verify-email', '/terms', '/privacy'];
  const isPublicRoute = publicRoutes.some(route => location.pathname.startsWith(route));

  // Routes that can be accessed with or without authentication
  const mixedRoutes = ['/paywall', '/subscription/success', '/onboarding', '/signup'];
  const isMixedRoute = mixedRoutes.some(route => location.pathname.startsWith(route));

  // Wait for auth to resolve before redirecting — prevents race condition where
  // mock/async auth fires after the initial render and causes a double-redirect.
  if (isLoading && !isPublicRoute && !isMixedRoute) {
    return null;
  }

  // If not authenticated and not on a public or mixed route, redirect to login
  if (!isAuthenticated && !isPublicRoute && !isMixedRoute) {
    return <Navigate to="/login" replace />;
  }

  // If authenticated and on login, redirect to dashboard
  // Note: /signup is excluded — the signup page handles its own navigation to /onboarding
  if (isAuthenticated && location.pathname === '/login') {
    return <Navigate to="/" replace />;
  }

  // Email-verification wall: hold an authenticated-but-unverified user on the
  // verification screen until they click the link. This precedes the mixed/app
  // routes so it also gates the onboarding wizard (verify before onboarding).
  // Grandfathered users are already verified; dev-bypass and offline-grace
  // sessions are treated as verified so they're never walled.
  const emailVerified = DEV_BYPASS || graceMode || user?.emailVerified === true;
  if (isAuthenticated && !emailVerified) {
    return (
      <Routes>
        <Route path="/verify-email" element={<VerifyEmail />} />
        <Route path="/verify-pending" element={<VerifyEmailWall />} />
        <Route path="/terms" element={<Terms />} />
        <Route path="/privacy" element={<Privacy />} />
        <Route path="*" element={<Navigate to="/verify-pending" replace />} />
      </Routes>
    );
  }

  // Send an owner who never finished setup back to the wizard. Deliberately
  // conservative: owners only (the survey is clinic-level), never while the
  // check is loading or failed, and never offline — an unknown answer must not
  // bounce a working session. The decision is `auth/onboardingGate.ts`: offline
  // the profile above is the replica users row, which never carries `clinic`,
  // so "no stamp" there is UNKNOWN, not "never completed" — reading it as
  // incomplete bounced owners into a wizard that cannot submit offline (the
  // 2026-09-11 field bounce). The wizard's close button submits only online.
  const needsOnboarding = decideNeedsOnboarding({
    isAuthenticated,
    emailVerified,
    graceMode,
    online: !OFFLINE_ENABLED || isEffectivelyOnline(),
    checkFailed: onboardingCheckFailed,
    profile: onboardingUser,
  });
  if (needsOnboarding && !location.pathname.startsWith('/onboarding')) {
    return <Navigate to="/onboarding" replace />;
  }

  // Handle mixed routes (paywall, subscription success, onboarding, signup)
  if (isMixedRoute) {
    return (
      <Routes>
        <Route path="/paywall" element={<Paywall />} />
        <Route path="/onboarding" element={<Onboarding />} />
        <Route path="/subscription/success" element={<SubscriptionSuccess />} />
        <Route path="/signup" element={<Signup />} />
      </Routes>
    );
  }

  // Public routes (not authenticated)
  if (!isAuthenticated) {
    return (
      <Routes>
        <Route path="/login" element={<Login />} />
        <Route path="/signup" element={<Signup />} />
        <Route path="/setup" element={<AccountSetup />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/verify-email" element={<VerifyEmail />} />
        <Route path="/terms" element={<Terms />} />
        <Route path="/privacy" element={<Privacy />} />
        <Route path="*" element={<Navigate to="/login" replace />} />
      </Routes>
    );
  }

  // Authenticated routes with subscription guard. DeliveryAttestationGuard
  // (VALSOFT proof-of-delivery) sits inside SubscriptionGuard so the paywall/
  // checkout/success/onboarding mixed-routes above are never walled — the
  // attestation appears when a paid clinic's owner/admin lands back in the app.
  return (
    <SubscriptionGuard>
      <DeliveryAttestationGuard>
        <ReleaseNotesProvider>
          <OnboardingProvider>
            <FaroUserSync />
            <AuthenticatedLayout />
          </OnboardingProvider>
        </ReleaseNotesProvider>
      </DeliveryAttestationGuard>
    </SubscriptionGuard>
  );
};

const App = () => {
  return (
    <AuthProvider>
      <AppContent />
      {/* ADR-0017: absolute-session-lifetime re-auth (warn + in-place lock). */}
      <SessionReauthDialog />
      {/* The workstation PIN lock (beta, off unless the clinic turns it on). */}
      <WorkstationLock />
    </AuthProvider>
  );
};

export default App; 